import { useLocation, Link } from "wouter";
import {
  Bookmark,
  Sparkles,
  CheckCircle2,
  Clock,
  ArrowDown,
  ArrowUpRight,
  ChevronDown,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { ItemRow } from "@/components/ItemRow";
import { EmptyState } from "@/components/EmptyState";
import { Thumb } from "@/components/Thumb";
import { CaptureButton } from "@/components/PageHeader";
import { useItemsStore } from "@/lib/stores/itemsStore";
import { useAuthStore } from "@/lib/stores/authStore";
import { splitMeta, formatWhen } from "@/lib/format";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 20) return "Buenas tardes";
  return "Buenas noches";
};

const StatCard = ({ label, value, icon: Icon, trend, trendTone, spin, testId }) => (
  <div className="rounded-lg border bg-card p-4 hover:border-teal-300/60 transition-colors" data-testid={testId}>
    <p className="flex items-center gap-2 text-[13px] text-muted-foreground">
      <Icon className={cn("h-4 w-4", spin && "animate-spin text-warning")} strokeWidth={1.75} />
      {label}
    </p>
    <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight">{value}</p>
    {trend && (
      <p className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
        {trendTone === "down" ? (
          <ArrowDown className="h-3 w-3 text-amber-700" strokeWidth={2} />
        ) : (
          <ArrowUpRight className="h-3 w-3 text-teal-700" strokeWidth={2} />
        )}
        <span className={trendTone === "down" ? "text-amber-700 font-medium" : "text-teal-700 font-medium"}>
          {trend.split(" ")[0]}
        </span>
        {trend.split(" ").slice(1).join(" ")}
      </p>
    )}
  </div>
);

const RailCard = ({ title, action, onAction, children, footer }) => (
  <section className="rounded-lg border bg-card">
    <div className="flex items-center justify-between px-4 py-3 border-b">
      <h2 className="text-sm font-semibold">{title}</h2>
      {action && (
        <button type="button" onClick={onAction} className="text-[13px] text-primary hover:underline">
          {action}
        </button>
      )}
    </div>
    <div className="p-4">{children}</div>
    {footer && <div className="px-4 py-2.5 border-t text-xs text-muted-foreground">{footer}</div>}
  </section>
);

export default function Inbox() {
  const { items, projects, select, booted } = useItemsStore();
  const user = useAuthStore((s) => s.user);
  const [, navigate] = useLocation();

  const pending = items
    .filter((i) => i.status === "inbox" || i.status === "pendiente")
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const enrichingItems = items.filter((i) => i.analyzeStatus === "enriqueciendo");
  const enriched7d = items.filter(
    (i) => i.analyzeStatus === "enriquecido" && Date.now() - new Date(i.createdAt) < 7 * 86400000,
  ).length;
  const lastEnriched = items
    .filter((i) => i.analyzeStatus === "enriquecido")
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 3);
  const firstName = (user?.name || "David").split(" ")[0];

  const openItem = (id) => {
    select(id);
    navigate("/biblioteca");
  };

  return (
    <div className="h-full overflow-y-auto scrollbar-thin" data-testid="inbox-page">
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between gap-4 animate-fade-up">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
              {greeting()}, {firstName} <span className="align-middle">👋</span>
            </h1>
            <p className="mt-1.5 text-muted-foreground" data-testid="inbox-subtitle">
              Tienes {pending.length} contenidos pendientes y {enrichingItems.length} en proceso de enriquecimiento.
            </p>
          </div>
          <CaptureButton />
        </div>

        <div className="mt-6 grid gap-6 xl:grid-cols-[1fr,340px] items-start">
          {/* Columna principal */}
          <div className="min-w-0 space-y-6">
            {!booted ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-28 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-fade-up">
                <StatCard label="Pendientes" value={pending.length} icon={Bookmark} trend="2 vs ayer" trendTone="down" testId="stat-inbox" />
                <StatCard label="Enriqueciendo" value={enrichingItems.length} icon={Sparkles} trend="1 vs ayer" testId="stat-enriching" spin={enrichingItems.length > 0} />
                <StatCard label="Enriquecidos (7 días)" value={enriched7d} icon={CheckCircle2} trend="18% vs semana pasada" testId="stat-enriched" />
                <StatCard label="Tiempo ahorrado" value="6,3 h" icon={Clock} trend="1,2 h vs semana pasada" testId="stat-total" />
              </div>
            )}

            <section>
              <div className="flex items-center gap-3 mb-3">
                <h2 className="text-lg font-semibold">
                  Pendientes de revisar{" "}
                  <span className="align-middle ml-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
                    {pending.length}
                  </span>
                </h2>
                <span className="flex-1" />
                <Link href="/biblioteca" className="text-[13px] text-primary hover:underline">
                  Ver todo
                </Link>
                <button
                  type="button"
                  onClick={() => toast("Orden configurable en la versión final.")}
                  className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-[13px] text-muted-foreground hover:text-foreground"
                >
                  <ArrowUpDown className="h-3.5 w-3.5" strokeWidth={1.75} /> Más recientes
                  <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
              </div>

              {!booted ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-[84px] rounded-lg" />
                  ))}
                </div>
              ) : pending.length === 0 ? (
                <div className="rounded-lg border bg-card">
                  <EmptyState icon={CheckCircle2} title="Todo revisado." hint="Comparte algo y te lo dejo listo." testId="inbox-empty" />
                </div>
              ) : (
                <div className="rounded-lg border bg-card divide-y overflow-hidden" data-testid="pending-list">
                  {pending.slice(0, 6).map((item) => (
                    <ItemRow key={item.id} item={item} flush onClick={() => openItem(item.id)} />
                  ))}
                  <Link
                    href="/biblioteca"
                    className="block w-full px-4 py-3 text-center text-[13px] text-primary hover:bg-secondary/60 transition-colors"
                  >
                    Ver todos los pendientes ({pending.length})
                  </Link>
                </div>
              )}
            </section>
          </div>

          {/* Raíl derecho */}
          <div className="space-y-4 xl:sticky xl:top-6">
            <RailCard
              title="Actividad del agente"
              action="Ver todo"
              onAction={() => navigate("/biblioteca")}
              footer="El agente trabaja en segundo plano. Te avisaremos cuando estén listos."
            >
              {enrichingItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">El agente está al día. Nada en proceso.</p>
              ) : (
                <div className="space-y-4">
                  <p className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-amber-600 animate-pulse" />
                    Enriqueciendo {enrichingItems.length} contenido{enrichingItems.length > 1 ? "s" : ""}…
                  </p>
                  {enrichingItems.slice(0, 3).map((i, idx) => (
                    <div key={i.id} className="flex items-start gap-2.5">
                      <Thumb platform={i.platform} size="mini" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{i.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {i.platform}
                          {splitMeta(i.meta).author ? ` · ${splitMeta(i.meta).author}` : ""}
                        </p>
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="h-1 flex-1 rounded-full bg-secondary overflow-hidden">
                            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${68 - idx * 34}%` }} />
                          </div>
                          <span className="text-[11px] text-muted-foreground tabular-nums">{68 - idx * 34}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </RailCard>

            <RailCard title="Proyectos activos" action="Ver todos" onAction={() => navigate("/proyectos")}>
              <div className="space-y-3">
                {projects
                  .filter((p) => p.estado !== "archivado")
                  .slice(0, 3)
                  .map((p) => {
                    const Icon = p.icon;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => navigate("/proyectos")}
                        className="w-full flex items-center gap-2.5 text-left hover:bg-secondary/60 rounded-md p-1 -m-1 transition-colors"
                      >
                        {Icon && (
                          <span className={cn("w-8 h-8 rounded-lg grid place-items-center shrink-0", p.iconCls)}>
                            <Icon className="h-4 w-4" strokeWidth={1.75} />
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium truncate">{p.name}</span>
                          <span className="block text-xs text-muted-foreground">
                            {p.itemCount} contenidos · {p.taskCount} tareas
                          </span>
                        </span>
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-600 shrink-0" />
                      </button>
                    );
                  })}
              </div>
            </RailCard>

            <RailCard
              title="Últimos enriquecidos"
              action="Ver todos"
              onAction={() => navigate("/biblioteca")}
              footer={
                <span className="flex items-center justify-center gap-1.5 w-full">
                  <Sparkles className="h-3 w-3" strokeWidth={1.75} /> Resumen generado por IA
                </span>
              }
            >
              <div className="space-y-3">
                {lastEnriched.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => openItem(i.id)}
                    className="w-full flex items-center gap-2.5 text-left hover:bg-secondary/60 rounded-md p-1 -m-1 transition-colors"
                  >
                    <Thumb platform={i.platform} size="mini" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium truncate">{i.title}</span>
                      <span className="block text-xs text-muted-foreground truncate">
                        {i.platform}
                        {splitMeta(i.meta).author ? ` · ${splitMeta(i.meta).author}` : ""}
                      </span>
                    </span>
                    <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">{formatWhen(i.createdAt)}</span>
                  </button>
                ))}
              </div>
            </RailCard>
          </div>
        </div>
      </div>
    </div>
  );
}
